import { Canvas, useFrame } from '@react-three/fiber';
import { Html, OrbitControls, Stars } from '@react-three/drei';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { positionAt } from '../lib/hermite';
import type { PlanetSpec } from '../store/planetsStore';
import { PLANETS, usePlanetsStore } from '../store/planetsStore';
import { useTimeline } from '../store/timelineStore';
import { useScale, scaleVec3 } from '../store/scaleStore';
import { useLayers, useSmallBodiesVisible } from '../store/layersStore';
import SmallBodies from './SmallBodies';
import Planet9Cloud from './Planet9Cloud';
import CameraDirector from './CameraDirector';
import { useSelection } from '../store/selectionStore';

function PlanetMesh({
  planet,
  hovered,
  onHover,
}: {
  planet: PlanetSpec;
  hovered: string | null;
  onHover: (id: string | null) => void;
}) {
  const ref = useRef<THREE.Mesh>(null!);
  // Subscription réactive : la planète n'est rendue qu'une fois les données arrivées.
  // Sans ça, le mesh reste à (0,0,0) le temps de la 1ʳᵉ frame et se superpose au Soleil.
  const points = usePlanetsStore((s) => s.vectors[planet.id]);
  const ready = !!points && points.length >= 2;

  // Position initiale calculée AVANT le premier rendu : pas de flash à l'origine.
  const initialPos = useMemo(() => {
    if (!ready) return null;
    const tJd = useTimeline.getState().tJd;
    const mode = useScale.getState().mode;
    const [x, y, z] = positionAt(points!, tJd);
    return scaleVec3([x, z, -y], mode);
  }, [ready, points]);

  useFrame((_state, delta) => {
    useTimeline.getState().advance(delta);
    if (!ready || !ref.current) return;
    const tJd = useTimeline.getState().tJd;
    const [x, y, z] = positionAt(points!, tJd);
    const mode = useScale.getState().mode;
    const [sx, sy, sz] = scaleVec3([x, z, -y], mode); // X, Z, -Y pour Y-up
    ref.current.position.set(sx, sy, sz);
  });

  if (!ready || !initialPos) return null;

  const isHovered = hovered === planet.id;

  return (
    <mesh
      ref={ref}
      position={initialPos}
      onPointerOver={(e) => {
        e.stopPropagation();
        onHover(planet.id);
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={() => {
        onHover(null);
        document.body.style.cursor = '';
      }}
      onClick={(e) => {
        e.stopPropagation();
        useSelection.getState().select({
          kind: 'planet',
          id: planet.id,
          name: planet.name,
        });
      }}
    >
      <sphereGeometry args={[planet.radius * (isHovered ? 1.6 : 1.0), 24, 16]} />
      <meshStandardMaterial
        color={planet.color}
        emissive={planet.color}
        emissiveIntensity={isHovered ? 0.55 : 0.25}
      />
      {isHovered && (
        <Html
          center
          distanceFactor={10}
          style={{
            pointerEvents: 'none',
            background: 'rgba(11, 13, 20, 0.92)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: '8px 12px',
            color: '#e8ecf2',
            fontSize: 12,
            whiteSpace: 'nowrap',
            transform: 'translate(0, -28px)',
          }}
        >
          <strong>{planet.name}</strong>
          <br />
          <span style={{ color: '#aab1bd' }}>
            JPL Horizons · target {planet.id}
          </span>
        </Html>
      )}
    </mesh>
  );
}

function Trail({ planetId, color }: { planetId: string; color: string }) {
  const points = usePlanetsStore((s) => s.vectors[planetId]);
  const mode = useScale((s) => s.mode);
  if (!points || points.length < 2) return null;
  const positions = new Float32Array(points.length * 3);
  for (let i = 0; i < points.length; i++) {
    const [sx, sy, sz] = scaleVec3(
      [points[i].x_au, points[i].z_au, -points[i].y_au],
      mode,
    );
    positions[i * 3] = sx;
    positions[i * 3 + 1] = sy;
    positions[i * 3 + 2] = sz;
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return (
    <line>
      <bufferGeometry attach="geometry" {...geom} />
      <lineBasicMaterial color={color} transparent opacity={0.35} />
    </line>
  );
}

function Sun({
  hovered,
  onHover,
}: {
  hovered: string | null;
  onHover: (id: string | null) => void;
}) {
  const isHovered = hovered === 'sun';
  return (
    <mesh
      onPointerOver={(e) => {
        e.stopPropagation();
        onHover('sun');
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={() => {
        onHover(null);
        document.body.style.cursor = '';
      }}
      onClick={(e) => {
        e.stopPropagation();
        useSelection.getState().select({ kind: 'planet', id: '10', name: 'Soleil' });
      }}
    >
      <sphereGeometry args={[0.4, 32, 24]} />
      <meshBasicMaterial color="#ffe0a3" />
      {isHovered && (
        <Html
          center
          distanceFactor={10}
          style={{
            pointerEvents: 'none',
            background: 'rgba(11, 13, 20, 0.92)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: '8px 12px',
            color: '#e8ecf2',
            fontSize: 12,
            whiteSpace: 'nowrap',
            transform: 'translate(0, -28px)',
          }}
        >
          <strong>Soleil</strong>
          <br />
          <span style={{ color: '#aab1bd' }}>centre du système — taille non à l'échelle</span>
        </Html>
      )}
    </mesh>
  );
}

function Loader() {
  const status = usePlanetsStore((s) => s.status);
  const error = usePlanetsStore((s) => s.error);
  if (status === 'ready') return null;
  return (
    <div className="hud-card" style={{ top: 16, left: 16 }}>
      {status === 'loading' && <>Chargement Horizons…</>}
      {status === 'idle' && <>Initialisation…</>}
      {status === 'error' && (
        <>
          <strong style={{ color: '#ff6b6b' }}>Erreur backend.</strong>
          <br />
          {error}
        </>
      )}
    </div>
  );
}

export default function SolarSystem() {
  const tJd = useTimeline((s) => s.tJd);
  const loadAround = usePlanetsStore((s) => s.loadAround);
  const [hovered, setHovered] = useState<string | null>(null);
  const showPlanets = useLayers((s) => s.active.planets);
  const showSmallBodies = useSmallBodiesVisible();
  const showPlanet9 = useLayers((s) => s.active.planet9);

  useEffect(() => {
    void loadAround(tJd, 365 * 2, 365 * 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Loader />
      <Canvas
        camera={{ position: [0, 18, 28], fov: 50, near: 0.01, far: 5000 }}
        gl={{ antialias: true }}
        style={{ background: '#02030a' }}
      >
        <ambientLight intensity={0.18} />
        <pointLight position={[0, 0, 0]} intensity={2.2} color="#ffe0a3" />
        <Stars radius={200} depth={50} count={5000} factor={4} fade />

        <Sun hovered={hovered} onHover={setHovered} />

        {showPlanets &&
          PLANETS.map((p) => (
            <group key={p.id}>
              <Trail planetId={p.id} color={p.color} />
              <PlanetMesh planet={p} hovered={hovered} onHover={setHovered} />
            </group>
          ))}

        <SmallBodies visible={showSmallBodies} />
        <Planet9Cloud visible={showPlanet9} />

        <OrbitControls makeDefault enablePan enableRotate enableZoom />
        <CameraDirector />
      </Canvas>
      {showSmallBodies && !showPlanet9 && (
        <div className="hud-card" style={{ bottom: 16, left: 16, maxWidth: 360, fontSize: 12 }}>
          <strong style={{ color: '#9fdcdc' }}>Corps au-delà de Neptune</strong>
          <span style={{ display: 'block', marginTop: 4, color: 'var(--text-dim)' }}>
            Cérès (2,8 UA), Pluton (39), Éris (68), Sedna (76→1006). Ces corps sont
            <strong> 5–500× plus loin que Mars</strong>.
            <br />
            Astuce : passe en <strong>échelle Log</strong> ↑ pour les voir tous, ou zoom out (molette).
          </span>
        </div>
      )}

      {showPlanet9 && (
        <div className="hud-card" style={{ bottom: 16, left: 16, maxWidth: 360 }}>
          <strong style={{ color: '#c08cff' }}>Hypothèse Planet 9</strong>
          <span style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            Nuage d'orbites candidates (a ≈ 380–800 UA). <strong>Aucune n'est confirmée.</strong>
            <br />
            <span style={{ color: 'var(--text-dim)' }}>
              Astuce : zoom out (molette) — les orbites sont 10× plus loin que Neptune.
            </span>
          </span>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              onClick={() => useSelection.getState().select({
                kind: 'planet', id: 'planet9', name: 'Planet 9',
              })}
              style={{
                flex: 1,
                background: 'rgba(192, 140, 255, 0.18)',
                border: '1px solid rgba(192, 140, 255, 0.5)',
                color: '#d4b3ff',
                padding: '6px 10px',
                fontSize: 11,
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Voir la fiche
            </button>
            <a
              href="/planet-nine"
              style={{
                flex: 1,
                textAlign: 'center',
                background: 'var(--bg-2)',
                border: '1px solid var(--line)',
                color: 'var(--text-1)',
                padding: '6px 10px',
                fontSize: 11,
                borderRadius: 6,
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Carte du ciel →
            </a>
          </div>
        </div>
      )}
    </>
  );
}
